import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Injector,
  input,
  Input,
  output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  ICellRendererParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable, firstValueFrom } from 'rxjs';
import {
  DocLineRole,
  DocLineUpdatedDto,
  SupplierPurchaseOrderDto,
  SupplierPurchaseOrderService,
} from '@digiwall/goliath-documents/proxy';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';
import { VatSystem, VatType } from '@digiwall/goliath-core/proxy';
import { ObserverQueue } from '@digiwall/core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { DocType } from '../../../base-docs/detail/doc-type';
import { FieldTypes, IHtmlCellRendererParams } from '@digiwall/ag-grid';
import { ColorizedIcon, ColorizedIconHelper } from '../../../model/colorized-icon.model';
import { PickPreparationModalComponent } from '../../../../components/pick-preparation/pick-preparation-modal.component';

export interface LinkedSupplierPreparationInfoDto {
  id: string;
  displayNumber: string;
  supplierPreparationStatusId: string;
}

@Component({
  selector: 'document-supplier-purchase-order-lines-grid',
  templateUrl: './supplier-purchase-order-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class SupplierPurchaseOrderLinesEditorComponent extends BaseThirdPartyDocLineEditor<SupplierPurchaseOrderDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  actionBtns: any[] = [];

  protected override handlingLinesUpdatedEvent = true;

  protected showDeleteAction = computed(() => {
    const selected = this.selectedLines();
    return selected.length > 0 && (selected as any[]).some(line => !line.hasActivePreparationLink);
  });

  @Input() set supplierPurchaseOrder(value) {
    this._document = value;
    this.setLanguageCode();

    this.actionBtns = [];
    if (this._document?.isPartiallyReceived) {
      this.actionBtns.push({
        label: this.localizationService.instant('Doc::SupplierPurchaseOrder:CancelQty'),
        action: () => {
          this.cancelledQuantity();
        },
      });
    }

    this.actionBtns.push(
      {
        label: this.localizationService.instant(
          'Doc::SupplierPurchaseOrder:UnlinkCustomerOrderLines',
        ),
        action: () => {
          this.unlinkCustomerOrderLines();
        },
      },
      { ...this.getPrintActionButton(), visibleInReadonly: true },
    );
  }

  @Input() set readonly(value) {
    this._readonly = value;
  }

  readonly thirdPartyVatSystem = input<VatSystem>(undefined);

  public entityUpdateRequested = output<void>();

  protected articleLineRole: DocLineRole = DocLineRole.SupplierPurchaseOrderArticle;
  protected textLineRole: DocLineRole = DocLineRole.DocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;
  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticlePurchasableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  constructor(
    localizationService: LocalizationService,
    private supplierPurchaseOrderService: SupplierPurchaseOrderService,
    injector: Injector,
    updateQueue: ObserverQueue,
    protected confirmationService: ConfirmationService,
    protected toastService: ToasterService,
  ) {
    super(localizationService, supplierPurchaseOrderService, injector, updateQueue);

    this.initColDefs();
  }

  protected override colDef_StockStatusIcons: ColDef = {
    colId: 'stockStatusIcons',
    pinned: 'left',
    width: 60,
    type: FieldTypes.Html,
    cellStyle: (p: any) =>
      p.data?.linkedPreparationInfos?.length > 0 ? { cursor: 'pointer' } : null,
    onCellClicked: (p: any) => this.viewLinkedPreparation(p.data?.linkedPreparationInfos),
    cellRendererParams: {
      src: (p: ICellRendererParams) => {
        if (p.data != null) {
          let htmlClasses: Array<ColorizedIcon> = [];
          if (p.data.totalReceivedQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-truck1', color: 'green' });
          }
          if (p.data.backorderQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-triangle', color: 'orange' });
          }
          if (p.data.cancelledQuantity > 0) {
            htmlClasses.push({ icon: 'digi-out-impossible', color: 'red' });
          }
          if (p.data.hasOrderLineLink) {
            htmlClasses.push({ icon: 'digi-out-user-client', color: 'darkred' });
          }
          if (p.data.hasActivePreparationLink) {
            const hasLessThanInProgress = (
              (p.data.linkedPreparationInfos as LinkedSupplierPreparationInfoDto[]) ?? []
            ).some(
              prep =>
                prep.supplierPreparationStatusId === 'Draft' ||
                prep.supplierPreparationStatusId === 'Ready',
            );
            htmlClasses.push({
              icon: 'digi-out-machinery',
              color: hasLessThanInProgress ? 'blue' : 'green',
            });
          }
          return ColorizedIconHelper.toHtml(htmlClasses);
        }
        return '';
      },
    } as IHtmlCellRendererParams,
  };

  /** Mirrors the customer order grid: jump to the linked preparation, asking which one if several. */
  private viewLinkedPreparation(preparations: LinkedSupplierPreparationInfoDto[]): void {
    if (!preparations?.length) return;

    if (preparations.length === 1) {
      this.navigateToPreparation(preparations[0].id);
      return;
    }

    const modalRef = this.modalService.open(PickPreparationModalComponent, {
      centered: true,
      injector: this.injector,
    });
    modalRef.componentInstance.preparations.set(preparations);
    modalRef.closed.subscribeOnce((selectedId: string) => {
      if (selectedId) {
        this.navigateToPreparation(selectedId);
      }
    });
  }

  private navigateToPreparation(preparationId: string): void {
    this.router.navigate(['../../supplier-preparations', preparationId], {
      relativeTo: this.route,
    });
  }

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.splice(
      this.columnDefs.findIndex(colDef => colDef.colId === BaseThirdPartyDocLineEditor.CODE_COL_ID),
      0,
      this.colDef_StockStatusIcons,
    );
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Purchase),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Purchase),
        this.colDef_TotalReceivedQuantity,
        this.colDef_RemainingQuantityToReceive,
        this.coldef_CancelledQuantity,
        ...(this.stockFeature.isEnabled
          ? [
              this.getColDef_TotalReceivedQuantityStock(),
              this.getColDef_RemainingQuantityToReceiveStock(),
              this.getColDef_CancelledQuantityStock(),
            ]
          : []),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
      ] as Array<ColDef>),
    );
  }

  isChapter = line => line.lineRole == DocLineRole.DocChapter;

  public override isCellEditable(params: EditableCallbackParams<any, any>) {
    let isEditable = true;

    if (params.data.lineRole == this.articleLineRole && params.colDef.field != 'code') {
      isEditable = params.data.articleId != null;
    }
    switch (params.colDef.field) {
      case 'remainingQuantityToReceived':
      case 'totalReceivedQuantity':
      case 'remainingQuantityToReceivedInStockUnit':
      case 'totalReceivedQuantityInStockUnit':
        isEditable = false;
        break;
    }

    // Lines linked to a non-archived preparation: only unit price and discount are editable
    if (params.data.hasActivePreparationLink) {
      const allowedCols = new Set(['unitPrice', 'articleStockUnitPrice', 'discount']);
      if (!allowedCols.has(params.colDef.field)) {
        isEditable = false;
      }
    }

    return (
      isEditable &&
      !params.data.hasOrderLineLink &&
      super.isCellEditable(params) &&
      !this._document.isPartiallyReceived
    );
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];

    return this.contextMenuItems(params);
  };

  protected override getDeleteMenuItem(line: any): (string | MenuItemDef)[] {
    if (line.hasActivePreparationLink || line.totalReceivedQuantity > 0) {
      return [];
    }
    return super.getDeleteMenuItem(line);
  }

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.supplierPurchaseOrderService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.supplierPurchaseOrderService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.supplierPurchaseOrderService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.supplierPurchaseOrderService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };

  protected async cancelledQuantity() {
    const selectedArticleLines = this.selectedLines().filter(
      line => line.lineRole == DocLineRole.SupplierPurchaseOrderArticle,
    );
    const ids = selectedArticleLines.map(line => line.id);
    if (ids == null || ids.length === 0) {
      return;
    }
    // UX guard: cannot settle lines linked to an active preparation (backend will also enforce this)
    if ((selectedArticleLines as any[]).some(line => line.hasActivePreparationLink)) {
      this.toastService.error('Doc::Doc:CannotSettleLineWithActivePreparation');
      return;
    }
    if (await this.wantToCancelQuantityForDelivery()) {
      this._updateQueue
        .enqueue(() => this.supplierPurchaseOrderService.cancelLinesOrder(this._document.id, ids))
        .subscribe(docWithLinesUpdated => {
          this.toastService.success('Doc::CancelQuantityToReceiveSuccessfully');
          this._updateLines(docWithLinesUpdated);
        });
    }
  }

  private async wantToCancelQuantityForDelivery() {
    let status = await firstValueFrom(
      this.confirmationService.warn('Doc::WantToCancelQuantityForOrder', '::AreYouSure'),
    );
    return status == Confirmation.Status.confirm;
  }

  private unlinkCustomerOrderLines() {
    this.supplierPurchaseOrderService
      .unlinkCustomerOrderLines(this.selectedLines().map(line => line.id))
      .subscribe(deletedLinkCount => {
        this.toastService.success(
          this.localizationService.instant(
            'Doc::SupplierPurchaseOrder:UnlinkCustomerOrderLinesSuccessfully',
            deletedLinkCount.toString(),
          ),
        );
        this.lineGrid.grid.grid.api.deselectAll();
        this.entityUpdateRequested.emit();
      });
  }
}
